import { useEffect, useState, type RefObject } from "react";

/** How far the pointer may move between press and release for it to count as a click, not a drag (which pans). */
export const CLICK_SLOP_PX = 5;

/**
 * What the wheel over a chart does: "page" scrolls the page, "hint" is the same with the pointer over the chart (the
 * widget says a click captures it), "chart" zooms the chart.
 */
export type WheelState = "page" | "hint" | "chart";

/**
 * The wheel over a chart scrolls the page until the chart is clicked (owner's report #19, 2026-10-23). A click
 * captures it: the wheel then zooms the chart and the widget shows a green outline. Clicking the chart again,
 * clicking anywhere else or Escape releases it. Ctrl (⌘ on a Mac) zooms at once without capturing. A drag pans the
 * chart either way and is not a click. ECharts' inside zoom takes every wheel event over the plot, whatever its
 * zoomOnMouseWheel says (it shares one controller per chart), so this holds those events back from the chart in the
 * capture phase on `ref`, the element around the chart's own. A touch screen has no wheel: a finger drags to pan and
 * two fingers pinch to zoom at once (web-on-phones WOP-06), so a tap neither captures nor shows the hint.
 */
export function useWheelCapture(ref: RefObject<HTMLElement | null>, enabled: boolean): WheelState {
  const [captured, setCaptured] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let armed = false;
    let down: { x: number; y: number } | undefined;
    const set = (v: boolean) => {
      armed = v;
      setCaptured(v);
    };
    const onWheel = (e: WheelEvent) => {
      if (armed || e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
    };
    const onDown = (e: PointerEvent) => {
      down = e.button === 0 && e.pointerType !== "touch" ? { x: e.clientX, y: e.clientY } : undefined;
    };
    const onUp = (e: PointerEvent) => {
      const start = down;
      down = undefined;
      if (!start || e.button !== 0) return;
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) <= CLICK_SLOP_PX) set(!armed);
    };
    // A press anywhere outside the chart releases it.
    const onOutside = (e: PointerEvent) => {
      if (armed && !(e.target instanceof Node && el.contains(e.target))) set(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (armed && e.key === "Escape") set(false);
    };
    const onEnter = (e: PointerEvent) => setOver(e.pointerType !== "touch");
    const onLeave = () => setOver(false);
    el.addEventListener("wheel", onWheel, { capture: true, passive: true });
    el.addEventListener("pointerdown", onDown, { capture: true });
    el.addEventListener("pointerup", onUp, { capture: true });
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
    document.addEventListener("pointerdown", onOutside, { capture: true });
    document.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("wheel", onWheel, { capture: true });
      el.removeEventListener("pointerdown", onDown, { capture: true });
      el.removeEventListener("pointerup", onUp, { capture: true });
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("pointerdown", onOutside, { capture: true });
      document.removeEventListener("keydown", onKey);
      setCaptured(false);
      setOver(false);
    };
  }, [ref, enabled]);
  if (!enabled) return "page";
  return captured ? "chart" : over ? "hint" : "page";
}
